/** @jsx jsx */
import { jsx } from '@emotion/core'
import { VFlow } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { startOfDay } from 'date-fns'
import { useAgendamentosCidadaoPagedQuery } from 'graphql/hooks.generated'
import { SituacaoAgendadoEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useMemo } from 'react'
import { GRUPO_CBO_ACOMP_PRE_NATAL, isCboDentista } from 'types/NivelCbo'
import { AgendamentoCidadaoModel } from 'view/agenda/cidadao/model-cidadao'

import { AgendamentoCidadaoSection } from '../../../../../agenda/cidadao/list/AgendamentoCidadaoSection'
import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'

interface ProximosAgendamentosCardProps {
  cidadaoId: ID
  agendamentosPath: string
}

export function ProximosAgendamentosCard(props: Readonly<ProximosAgendamentosCardProps>) {
  const { cidadaoId, agendamentosPath } = props
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()
  const today = startOfDay(now)

  const { data, loading } = useAgendamentosCidadaoPagedQuery({
    variables: {
      input: {
        cidadaoId: cidadaoId,
        dataAgendadoInicio: today,
        dataAgendadoFim: null,
        situacao: [SituacaoAgendadoEnum.AGENDADO],
        maisNovosPrimeiro: false,
        cbosToFilter: [...GRUPO_CBO_ACOMP_PRE_NATAL],
      },
    },
  })

  const agendamentos = useMemo(() => data?.agendados?.content, [data])

  const agendamentosFiltrados = useMemo(() => {
    const agendamentoDentista = agendamentos?.find((valor) => isCboDentista(valor.lotacaoAgendada?.cbo?.cbo2002))

    const agendamentosMedicoEnfermeiro = agendamentos?.filter(
      (valor) => !isCboDentista(valor.lotacaoAgendada?.cbo?.cbo2002)
    )

    if (agendamentosMedicoEnfermeiro) {
      return agendamentoDentista
        ? [agendamentosMedicoEnfermeiro[0], agendamentoDentista]
        : agendamentosMedicoEnfermeiro.slice(0, 2)
    }

    return agendamentoDentista ? [agendamentoDentista] : []
  }, [agendamentos])

  const renderAgendamentoRow = (index: number, agendamento: AgendamentoCidadaoModel) => {
    return (
      <Fragment>
        {!!index && <Divider />}
        <AgendamentoCidadaoSection agendamento={agendamento} isAgendaOnlineAtiva={data?.conexao.agendaOnline.ativa} />
      </Fragment>
    )
  }

  const hasAgendamentos = agendamentos?.length > 0

  return (
    <AcompanhamentoCard
      id='proximos-agendamentos'
      title='Próximo(s) Agendamento(s)'
      loading={loading}
      placeholder='Nenhum agendamento para enfermeiro/médico/dentista registrado'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar todos' path={agendamentosPath} />}
      justifyHeaderContent='space-between'
      bodyPadding={hasAgendamentos ? '0' : undefined}
    >
      {hasAgendamentos && (
        <VFlow vSpacing={0}>
          {agendamentosFiltrados.map((agendamento: AgendamentoCidadaoModel, index) =>
            renderAgendamentoRow(index, agendamento)
          )}
        </VFlow>
      )}
    </AcompanhamentoCard>
  )
}
